import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { submissionsService } from '../../../services/submissionsService';
import { paymentsService } from '../../../services/paymentsService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Build a short preview string from submissionData for the list row
const buildPreview = (submissionData, formElements, maxChars = 60) => {
  if (!submissionData || typeof submissionData !== 'object') return '—';
  const inputTypes = new Set(['input', 'text', 'email', 'phone', 'number', 'url', 'textarea', 'select', 'dropdown', 'radio']);
  const parts = [];
  for (const el of formElements) {
    if (!inputTypes.has(el?.type)) continue;
    const val = submissionData[el?.id];
    if (!val || (Array.isArray(val) && val.length === 0)) continue;
    const display = Array.isArray(val) ? val.join(', ') : String(val);
    parts.push(display);
    if (parts.join(' · ').length >= maxChars) break;
  }
  const joined = parts.join(' · ');
  return joined.length > maxChars ? joined.slice(0, maxChars - 1) + '…' : joined || '(no text fields)';
};

// Render a single submitted value (handles arrays, strings, etc.)
const renderValue = (value) => {
  if (value === undefined || value === null || value === '') return <span className="text-muted-foreground italic">—</span>;
  if (Array.isArray(value)) {
    return <span>{value.join(', ') || <span className="text-muted-foreground italic">—</span>}</span>;
  }
  return <span className="break-words">{String(value)}</span>;
};

// Payment status badge styling
const paymentStatusConfig = {
  succeeded: { bg: 'bg-green-100', text: 'text-green-700', icon: 'CheckCircle', label: 'Paid' },
  pending:   { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'Clock', label: 'Pending' },
  failed:    { bg: 'bg-red-100', text: 'text-red-700', icon: 'XCircle', label: 'Failed' },
  canceled:  { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'MinusCircle', label: 'Canceled' },
};

const PaymentBadge = ({ status, amount, currency }) => {
  const cfg = paymentStatusConfig[status] || paymentStatusConfig.pending;
  const formatted = amount != null
    ? paymentsService.formatAmount(amount, currency || 'USD')
    : null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon name={cfg.icon} size={10} />
      {cfg.label}{formatted ? ` · ${formatted}` : ''}
    </span>
  );
};

// ── Non-input field types to skip in detail view ──────────────────────────────
const LAYOUT_TYPES = new Set(['heading', 'paragraph', 'divider', 'media', 'section', 'columns', 'icon', 'progress', 'stepper', 'image']);

// ── Sub-components ────────────────────────────────────────────────────────────

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
    <Icon name="Loader2" size={24} className="animate-spin" />
    <p className="text-sm">Loading responses…</p>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center">
    <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
      <Icon name="AlertCircle" size={18} color="var(--color-destructive)" />
    </div>
    <p className="text-sm text-destructive">{message}</p>
    <button
      onClick={onRetry}
      className="text-xs text-primary hover:underline"
    >
      Try again
    </button>
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center">
    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
      <Icon name="Inbox" size={22} color="var(--color-muted-foreground)" />
    </div>
    <p className="text-sm font-medium text-foreground">No responses yet</p>
    <p className="text-xs text-muted-foreground leading-relaxed">
      Responses will appear here once your form is published and someone fills it out.
    </p>
  </div>
);

const UnsavedState = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center">
    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
      <Icon name="FileText" size={22} color="var(--color-muted-foreground)" />
    </div>
    <p className="text-sm font-medium text-foreground">Save your form first</p>
    <p className="text-xs text-muted-foreground leading-relaxed">
      Save and publish the form to start collecting responses.
    </p>
  </div>
);

// ── Detail view for a single submission ───────────────────────────────────────

const SubmissionDetail = ({ submission, formElements, onBack, paymentTransaction }) => {
  const data = submission?.submissionData || {};

  // Build ordered field list: follow formElements order, skip layout types
  const fields = (formElements || []).filter((el) => !LAYOUT_TYPES.has(el?.type));

  // Also show any keys in submissionData that aren't in formElements (edge case)
  const knownIds = new Set(fields.map((el) => el?.id));
  const extraKeys = Object.keys(data).filter((k) => !knownIds.has(k));

  return (
    <div className="flex flex-col h-full">
      {/* Detail header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1 rounded hover:bg-muted transition-colors"
          title="Back to list"
        >
          <Icon name="ArrowLeft" size={16} color="var(--color-muted-foreground)" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">Response detail</p>
          <p className="text-xs text-muted-foreground">{formatDate(submission?.createdAt)}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
          submission?.status === 'completed'
            ? 'bg-green-100 text-green-700'
            : 'bg-muted text-muted-foreground'
        }`}>
          {submission?.status || 'completed'}
        </span>
      </div>

      {/* Payment info card (if payment form) */}
      {paymentTransaction && (
        <div className="mx-4 mt-4 rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="CreditCard" size={13} color="var(--color-muted-foreground)" />
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Payment</p>
          </div>
          <div className="flex items-center justify-between">
            <PaymentBadge
              status={paymentTransaction.status}
              amount={paymentTransaction.amount}
              currency={paymentTransaction.currency}
            />
          </div>
          <div className="space-y-1 pt-1 border-t border-border/50">
            {paymentTransaction.customerEmail && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Customer email</span>
                <span className="text-foreground truncate ml-2">{paymentTransaction.customerEmail}</span>
              </div>
            )}
            {paymentTransaction.customerName && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Customer name</span>
                <span className="text-foreground truncate ml-2">{paymentTransaction.customerName}</span>
              </div>
            )}
            {paymentTransaction.paymentIntentId && (
              <div className="flex justify-between text-xs gap-2">
                <span className="text-muted-foreground flex-shrink-0">Payment ID</span>
                <span className="text-foreground font-mono text-[10px] truncate">{paymentTransaction.paymentIntentId}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Field values */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {fields.length === 0 && extraKeys.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">No field data available.</p>
        )}

        {fields.map((el) => (
          <div key={el?.id} className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {el?.label || el?.name || el?.id}
              {el?.required && <span className="text-destructive ml-0.5">*</span>}
            </p>
            <div className="text-sm text-foreground bg-muted/40 rounded-md px-3 py-2 min-h-[32px] flex items-start">
              {renderValue(data[el?.id])}
            </div>
          </div>
        ))}

        {extraKeys.map((key) => (
          <div key={key} className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{key}</p>
            <div className="text-sm text-foreground bg-muted/40 rounded-md px-3 py-2 min-h-[32px] flex items-start">
              {renderValue(data[key])}
            </div>
          </div>
        ))}

        {/* Metadata */}
        <div className="pt-2 border-t border-border/50 space-y-1">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Metadata</p>
          {submission?.source && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Source</span>
              <span className="text-foreground">{submission.source}</span>
            </div>
          )}
          {submission?.createdAt && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Submitted</span>
              <span className="text-foreground">{formatDate(submission.createdAt)}</span>
            </div>
          )}
          {submission?.id && (
            <div className="flex justify-between text-xs gap-2">
              <span className="text-muted-foreground flex-shrink-0">ID</span>
              <span className="text-foreground font-mono text-[10px] truncate">{submission.id}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────

const SubmissionsPanel = ({ formId, formElements = [], form = null }) => {
  const [submissions, setSubmissions] = useState([]);
  const [blockedCount, setBlockedCount] = useState(0);
  const [paymentMap, setPaymentMap] = useState({}); // submissionId -> transaction
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const isPaymentForm = !!(form?.paymentEnabled);

  const fetchData = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch submissions and (if payment form) transactions in parallel
      const [subs, txns, blocked] = await Promise.all([
        submissionsService.getSubmissionsByFormId(formId),
        isPaymentForm ? paymentsService.getPaymentTransactions(formId).catch(() => []) : Promise.resolve([]),
        submissionsService.getBlockedSubmissionCount(formId).catch(() => 0),
      ]);

      setSubmissions(subs || []);
      setBlockedCount(blocked || 0);

      // Build a map from submission_id -> transaction for quick lookup
      // paymentsService.getPaymentTransactions returns raw snake_case from Supabase
      // We normalise to camelCase keys here for consistency.
      const map = {};
      for (const txn of txns) {
        const subId = txn.submission_id || txn.submissionId;
        if (subId) {
          map[subId] = {
            status: txn.status,
            amount: txn.amount,
            currency: txn.currency,
            customerEmail: txn.customer_email || txn.customerEmail,
            customerName: txn.customer_name || txn.customerName,
            paymentIntentId: txn.payment_intent_id || txn.paymentIntentId,
            createdAt: txn.created_at || txn.createdAt,
          };
        }
      }
      setPaymentMap(map);
    } catch (err) {
      console.error('SubmissionsPanel fetch error:', err);
      setError(err?.message || 'Failed to load responses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formId, isPaymentForm]);

  const handleDeleteConfirm = useCallback(async (submissionId) => {
    setDeletingId(submissionId);
    try {
      await submissionsService.deleteSubmission(submissionId, formId);
      setSubmissions((prev) => prev.filter((s) => s.id !== submissionId));
    } catch (err) {
      console.error('Delete submission error:', err);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }, [formId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute total collected for payment forms
  const totalCollected = isPaymentForm
    ? Object.values(paymentMap)
        .filter((t) => t.status === 'succeeded')
        .reduce((sum, t) => sum + (t.amount || 0), 0)
    : 0;
  const paymentCurrency = form?.paymentSettings?.currency || 'USD';

  // ── No formId (unsaved form) ──────────────────────────────────────────────
  if (!formId) {
    return <UnsavedState />;
  }

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selectedSubmission) {
    const paymentTxn = paymentMap[selectedSubmission.id] || null;
    return (
      <SubmissionDetail
        submission={selectedSubmission}
        formElements={formElements}
        onBack={() => setSelectedSubmission(null)}
        paymentTransaction={paymentTxn}
      />
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Toolbar: count + refresh */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {loading ? 'Loading…' : `${submissions.length} response${submissions.length !== 1 ? 's' : ''}`}
        </span>
        {!loading && blockedCount > 0 && (
          <span className="ml-auto mr-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
            <Icon name="ShieldAlert" size={11} />
            {blockedCount} blocked
          </span>
        )}
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <Icon
            name="RefreshCw"
            size={13}
            color="var(--color-muted-foreground)"
            className={loading ? 'animate-spin' : ''}
          />
        </button>
      </div>

      {/* Payment summary bar — only shown for payment forms with at least one succeeded payment */}
      {isPaymentForm && !loading && !error && submissions.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border-b border-green-100">
          <Icon name="DollarSign" size={13} color="#15803d" />
          <span className="text-xs font-medium text-green-700">
            Total collected:{' '}
            <span className="font-semibold">
              {paymentsService.formatAmount(totalCollected, paymentCurrency)}
            </span>
          </span>
          <span className="ml-auto text-[10px] text-green-600">
            {Object.values(paymentMap).filter((t) => t.status === 'succeeded').length} paid
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} onRetry={fetchData} />}
        {!loading && !error && submissions.length === 0 && <EmptyState />}
        {!loading && !error && submissions.length > 0 && (
          <ul className="divide-y divide-border/50">
            {submissions.map((sub) => {
              const preview = buildPreview(sub?.submissionData, formElements);
              const txn = paymentMap[sub?.id];
              const isConfirming = confirmDeleteId === sub?.id;
              const isDeleting = deletingId === sub?.id;
              return (
                <li key={sub?.id} className="group relative">
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                    onClick={() => !isConfirming && setSelectedSubmission(sub)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDate(sub?.createdAt)}
                      </p>
                      <Icon
                        name="ChevronRight"
                        size={14}
                        color="var(--color-muted-foreground)"
                        className="flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    <p className="text-sm text-foreground mt-1 leading-snug line-clamp-2">{preview}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {sub?.status && sub.status !== 'completed' && (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {sub.status}
                        </span>
                      )}
                      {txn && (
                        <PaymentBadge
                          status={txn.status}
                          amount={txn.amount}
                          currency={txn.currency}
                        />
                      )}
                    </div>
                  </button>

                  {/* Delete affordance */}
                  {isConfirming ? (
                    <div className="absolute inset-0 flex items-center justify-end gap-1.5 px-3 bg-destructive/5 border border-destructive/20 rounded">
                      <span className="text-xs text-destructive mr-1">Delete this response?</span>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors"
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteConfirm(sub?.id)}
                        className="text-xs px-2 py-1 rounded bg-destructive hover:bg-destructive/90 text-white transition-colors flex items-center gap-1 disabled:opacity-60"
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Icon name="Loader2" size={11} className="animate-spin" />
                        ) : (
                          <Icon name="Trash2" size={11} />
                        )}
                        Delete
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(sub?.id); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-destructive/10 transition-all"
                      title="Delete response"
                    >
                      <Icon name="Trash2" size={13} color="var(--color-muted-foreground)" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SubmissionsPanel;
